import { Box } from "@mui/material";
import { useMemo, useState } from "react";

import Flex from "../../../../../components/Flex";
import OutlineCard from "../../../../../components/OutlineCard";
import Text from "../../../../../components/Text";
import { CustomSelect } from "../../../../../components/CustomSelect";
import StyledButtonV2 from "../../../../../components/app/StyledButtonV2";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import {
  DEFAULT_LABEL_COLOR,
  DEFAULT_LABEL_COLOR_2,
  getXtenantId,
  showToastOnApiCall,
} from "../../../../../utils/utils";
import { useTenantConfiguration } from "../../../../../queries/use-get-tenant-configuration";
import { useUpdateTenantConfiguration } from "../../../../../queries/admin/use-update-tenant-configuration";
import { useCreateTenantConfiguration } from "../../../../../queries/admin/use-create-tenant-configurations";
import { TenantConfigurationInvoiceReminderDurationEnum } from "../../../../../services";

type InvoiceReminderDuration = TenantConfigurationInvoiceReminderDurationEnum;

const DURATION_OPTIONS = [
  {
    label: "7 Days",
    value: TenantConfigurationInvoiceReminderDurationEnum.Days7,
  },
  {
    label: "15 Days",
    value: TenantConfigurationInvoiceReminderDurationEnum.Days15,
  },
  {
    label: "1 Month",
    value: TenantConfigurationInvoiceReminderDurationEnum.Months1,
  },
  {
    label: "2 Months",
    value: TenantConfigurationInvoiceReminderDurationEnum.Months2,
  },
  {
    label: "3 Months",
    value: TenantConfigurationInvoiceReminderDurationEnum.Months3,
  },
];

const InvoiceReminderConfigurationForm = () => {
  const showSnackbar = useSnackbar();
  const xTENANTID = getXtenantId() ?? "";

  const { data: tenantConfig, refetch } = useTenantConfiguration({
    providerGroupId: xTENANTID,
  });

  const { mutateAsync: updateTenantConfig } = useUpdateTenantConfiguration();
  const { mutateAsync: createTenantConfig } = useCreateTenantConfiguration();

  const record: any = useMemo(() => {
    if (!tenantConfig) return undefined;
    return (tenantConfig as any)?.data ?? tenantConfig;
  }, [tenantConfig]);

  const existingUuid: string | undefined = record?.uuid;
  const savedDuration: InvoiceReminderDuration | undefined =
    record?.invoiceReminderDuration;

  const [draft, setDraft] = useState<InvoiceReminderDuration | undefined>(
    undefined
  );

  const value = draft ?? savedDuration;
  const isDirty = draft !== undefined && draft !== savedDuration;

  const handleSave = () => {
    if (!value) return;

    const payload = {
      ...(record ?? {}),
      uuid: existingUuid,
      invoiceReminderDuration: value,
    };

    const apiCall = existingUuid
      ? updateTenantConfig({ configuration: payload, xTENANTID })
      : createTenantConfig({ configuration: payload, xTENANTID });

    showToastOnApiCall({
      apiCall,
      showSnackbar,
      onSave: () => {
        setDraft(undefined);
        refetch();
      },
    });
  };

  return (
    <OutlineCard sx={{ height: "100%" }}>
      <Flex flexDirection={"column"} gap={"4px"} marginBottom={"16px"}>
        <Text fontSize={"16px"} fontWeight={600} color={DEFAULT_LABEL_COLOR}>
          Invoice Reminder Configuration
        </Text>
        <Text fontSize={"13px"} color={DEFAULT_LABEL_COLOR_2}>
          Configure the invoice due duration. Invoice due dates are calculated
          from this setting, and overdue invoices automatically notify patients
          through their preferred communication channel.
        </Text>
      </Flex>

      <Box sx={{ padding: "20px 0" }}>
        <Flex flexDirection={"column"} gap={"12px"}>
          <Text fontSize={"14px"} fontWeight={600} color={DEFAULT_LABEL_COLOR}>
            Invoice Reminder Duration
            <Text component={"span"} color={"error.main"}>
              {" *"}
            </Text>
          </Text>
          <Box width={"220px"}>
            <CustomSelect
              placeholder="Select duration"
              options={DURATION_OPTIONS}
              disableSortOptions
              value={value}
              onChange={(v) =>
                setDraft((v as InvoiceReminderDuration) || undefined)
              }
              clearable={false}
            />
          </Box>
        </Flex>
      </Box>

      <Flex justifyContent={"flex-end"} marginTop={"16px"}>
        <StyledButtonV2
          label="Save"
          onClick={handleSave}
          disabled={!isDirty || !value}
        />
      </Flex>
    </OutlineCard>
  );
};

export default InvoiceReminderConfigurationForm;
